import { useState } from 'react';
import { Button, Stack, Text } from '@repletoryreach/sdk-ui';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { admin } from '@repletoryreach/sdk-api';
import { useAuth } from '@repletoryreach/sdk-auth';
import { useQueryClient } from '@tanstack/react-query';
import { useT } from '../../../../i18n/I18nProvider';

export function ChangeScopeButton({
  userId,
  email,
  current
}: {
  userId: string;
  email: string;
  current: 'tenant' | 'platform';
}) {
  const t = useT();
  const qc = useQueryClient();
  const { user: signedIn } = useAuth();
  const [pending, setPending] = useState(false);
  const target: 'tenant' | 'platform' = current === 'platform' ? 'tenant' : 'platform';

  // Demoting yourself removes your own access to every /v1/admin route, and
  // there is no server-side guard to stop it, so the console will not offer it.
  const isSelfDemotion = target === 'tenant' && signedIn?.id === userId;

  function handleClick() {
    openConfirmModal({
      title: t(target === 'platform' ? 'users.view.scope.modal.titleToPlatform' : 'users.view.scope.modal.titleToTenant', { email }),
      children: (
        <Stack gap="sm">
          <Text fz="sm">
            {target === 'platform'
              ? t('users.view.scope.modal.bodyToPlatform')
              : t('users.view.scope.modal.bodyToTenant')}
          </Text>
          {/* The roles page says when the change bites; this one never did. */}
          <Text fz="sm" c="var(--rr-color-text-muted)">
            {t(target === 'platform' ? 'users.view.scope.modal.effectiveGain' : 'users.view.scope.modal.effectiveLose')}
          </Text>
        </Stack>
      ),
      labels: {
        confirm: t(target === 'platform' ? 'users.view.scope.modal.confirmToPlatform' : 'users.view.scope.modal.confirmToTenant'),
        cancel: t('action.cancel')
      },
      confirmProps: { color: target === 'platform' ? 'orange' : 'red' },
      onConfirm: async () => {
        setPending(true);
        try {
          await admin.updateUserScope(userId, target);
          await qc.invalidateQueries({ queryKey: ['admin', 'users'] });
          notifySuccess(
            t(target === 'platform' ? 'users.view.scope.toast.nowPlatform' : 'users.view.scope.toast.nowTenant', { email }),
            t('users.view.scope.toast.updatedTitle')
          );
        } catch (err) {
          // The failure title used to come from the billing catalogue, so
          // editing a SKU-rates string changed the copy on this page.
          notifyError(
            err instanceof Error ? err.message : t('users.view.scope.toast.failed'),
            t('users.view.scope.toast.failedTitle')
          );
        } finally {
          setPending(false);
        }
      }
    });
  }

  const label = t(target === 'platform' ? 'users.view.scope.button.toPlatform' : 'users.view.scope.button.toTenant');

  if (isSelfDemotion) {
    return (
      <Tooltip label={t('users.view.scope.selfDemotionBlocked')}>
        <span style={{ display: 'inline-flex' }}>
          <Button size="sm" variant="outline" disabled>{label}</Button>
        </span>
      </Tooltip>
    );
  }

  return (
    <Button size="sm" variant="outline" loading={pending} onClick={handleClick}>
      {label}
    </Button>
  );
}
